import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Rating,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Help as HelpIcon,
  Visibility as ExampleIcon,
} from '@mui/icons-material';
import solutionService from '../services/solution.service';
import { Skill, Solution } from '../types';

interface ExerciseEvaluationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (evaluation: ExerciseEvaluation) => void;
  exerciseId: number;
  patternId?: number;
  patternTitle?: string;
}

export interface ExerciseEvaluation {
  exerciseId: number;
  status: 'success_alone' | 'success_with_help' | 'success_with_example' | 'failed_followed' | 'failed';
  rating: number;
  remarks: string;
  selectedSolutionId: number | null;
  implementedSkills: number[]; // Skill IDs that were well executed
  poorlyExecutedSkills: number[]; // Skill IDs that were poorly executed
  missingJustifications: boolean;
}

const ExerciseEvaluationModal: React.FC<ExerciseEvaluationModalProps> = ({
  open,
  onClose,
  onSave,
  exerciseId,
  patternId,
  patternTitle,
}) => {
  const [status, setStatus] = useState<ExerciseEvaluation['status']>('success_alone');
  const [rating, setRating] = useState<number>(3);
  const [remarks, setRemarks] = useState<string>('');
  const [selectedSolutionId, setSelectedSolutionId] = useState<number | null>(null);
  const [missingJustifications, setMissingJustifications] = useState<boolean>(false);

  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [implementedSkills, setImplementedSkills] = useState<Set<number>>(new Set());
  const [poorlyExecutedSkills, setPoorlyExecutedSkills] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && patternId) {
      loadSolutions();
    } else {
      // Reset state when closing
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patternId]);

  const resetForm = () => {
    setStatus('success_alone');
    setRating(3);
    setRemarks('');
    setSelectedSolutionId(null);
    setMissingJustifications(false);
    setSolutions([]);
    setAllSkills([]);
    setImplementedSkills(new Set());
    setPoorlyExecutedSkills(new Set());
  };

  const loadSolutions = async () => {
    if (!patternId) return;

    try {
      setLoading(true);
      const allSolutions = await solutionService.getSolutions();
      // Filter solutions by pattern
      const solutionsData = allSolutions.filter((sol: any) => sol.pattern === patternId);
      setSolutions(solutionsData);

      // Extract all unique skills from all solutions
      const skillsMap = new Map<number, Skill>();
      solutionsData.forEach((solution) => {
        solution.skills?.forEach((skill: Skill) => {
          if (!skillsMap.has(skill.id)) {
            skillsMap.set(skill.id, skill);
          }
        });
      });
      setAllSkills(Array.from(skillsMap.values()));
    } catch (error) {
      console.error('Error loading solutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleImplementedSkill = (skillId: number) => {
    const newSet = new Set(implementedSkills);
    if (newSet.has(skillId)) {
      newSet.delete(skillId);
    } else {
      newSet.add(skillId);
      // Remove from poorly executed if present
      const poorSet = new Set(poorlyExecutedSkills);
      poorSet.delete(skillId);
      setPoorlyExecutedSkills(poorSet);
    }
    setImplementedSkills(newSet);
  };

  const handleTogglePoorlyExecutedSkill = (skillId: number) => {
    const newSet = new Set(poorlyExecutedSkills);
    if (newSet.has(skillId)) {
      newSet.delete(skillId);
    } else {
      newSet.add(skillId);
      // Remove from implemented if present
      const implSet = new Set(implementedSkills);
      implSet.delete(skillId);
      setImplementedSkills(implSet);
    }
    setPoorlyExecutedSkills(newSet);
  };

  const handleSave = () => {
    const evaluation: ExerciseEvaluation = {
      exerciseId,
      status,
      rating,
      remarks,
      selectedSolutionId,
      implementedSkills: Array.from(implementedSkills),
      poorlyExecutedSkills: Array.from(poorlyExecutedSkills),
      missingJustifications,
    };
    onSave(evaluation);
    onClose();
  };

  const getStatusConfig = (statusValue: ExerciseEvaluation['status']) => {
    switch (statusValue) {
      case 'success_alone':
        return { label: 'Réussi tout seul', icon: <SuccessIcon />, color: 'success' };
      case 'success_with_help':
        return { label: 'Réussi avec de l\'aide', icon: <HelpIcon />, color: 'primary' };
      case 'success_with_example':
        return { label: 'Réussi avec un exemple sous les yeux', icon: <ExampleIcon />, color: 'info' };
      case 'failed_followed':
        return { label: 'Non réussi mais a suivi mon corrigé', icon: <FailIcon />, color: 'warning' };
      case 'failed':
        return { label: 'Non réussi', icon: <FailIcon />, color: 'error' };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Évaluer l'exercice</Typography>
        {patternTitle && (
          <Typography variant="body2" color="text.secondary">
            Pattern : {patternTitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Status Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                  Statut de réalisation
                </FormLabel>
                <RadioGroup value={status} onChange={(e) => setStatus(e.target.value as ExerciseEvaluation['status'])}>
                  {(['success_alone', 'success_with_help', 'success_with_example', 'failed_followed', 'failed'] as const).map((statusValue) => {
                    const config = getStatusConfig(statusValue);
                    return (
                      <FormControlLabel
                        key={statusValue}
                        value={statusValue}
                        control={<Radio />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            {config.icon}
                            <Typography>{config.label}</Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Rating */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Note de l'exercice
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Rating
                  value={rating}
                  onChange={(_, newValue) => setRating(newValue || 1)}
                  max={5}
                  size="large"
                />
                <Typography variant="h6" color="primary">
                  {rating}/5
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Solution Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Solution choisie par l'élève
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  <ListItem
                    button
                    selected={selectedSolutionId === null}
                    onClick={() => setSelectedSolutionId(null)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary="Aucune des solutions référencées"
                      primaryTypographyProps={{ fontStyle: 'italic', color: 'text.secondary' }}
                    />
                  </ListItem>
                  {solutions.map((solution) => (
                    <ListItem
                      key={solution.id}
                      button
                      selected={selectedSolutionId === solution.id}
                      onClick={() => setSelectedSolutionId(solution.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        border: selectedSolutionId === solution.id ? '2px solid' : '1px solid',
                        borderColor: selectedSolutionId === solution.id ? 'primary.main' : 'divider',
                        bgcolor: selectedSolutionId === solution.id ? 'primary.50' : 'transparent',
                      }}
                    >
                      <ListItemText
                        primary={`Solution #${solution.id}`}
                        secondary={`${solution.skills?.length || 0} savoir-faire`}
                        sx={{ mb: 1, width: '100%' }}
                      />
                      {solution.skills && solution.skills.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 2, width: '100%' }}>
                          {solution.skills.map((skill) => (
                            <Chip
                              key={skill.id}
                              label={skill.name}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Skills Evaluation */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Évaluation des savoir-faire
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Sélectionnez les savoir-faire bien exécutés ou mal exécutés
              </Typography>

              {allSkills.length === 0 ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                  Aucun savoir-faire disponible pour ce pattern
                </Typography>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Grid container spacing={1}>
                    {allSkills.map((skill) => {
                      const isImplemented = implementedSkills.has(skill.id);
                      const isPoorlyExecuted = poorlyExecutedSkills.has(skill.id);

                      return (
                        <Grid item xs={12} key={skill.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: isImplemented ? 'success.50' : isPoorlyExecuted ? 'error.50' : 'transparent',
                              border: '1px solid',
                              borderColor: isImplemented ? 'success.main' : isPoorlyExecuted ? 'error.main' : 'divider',
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {skill.name}
                            </Typography>
                            <Button
                              size="small"
                              variant={isImplemented ? 'contained' : 'outlined'}
                              color="success"
                              onClick={() => handleToggleImplementedSkill(skill.id)}
                              sx={{ minWidth: 120 }}
                            >
                              Bien exécuté
                            </Button>
                            <Button
                              size="small"
                              variant={isPoorlyExecuted ? 'contained' : 'outlined'}
                              color="error"
                              onClick={() => handleTogglePoorlyExecutedSkill(skill.id)}
                              sx={{ minWidth: 120 }}
                            >
                              Mal exécuté
                            </Button>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Missing Justifications */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={missingJustifications}
                    onChange={(e) => setMissingJustifications(e.target.checked)}
                  />
                  <Typography variant="body1">
                    Des éléments de justification manquent dans la réponse fournie
                  </Typography>
                </Box>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Remarks */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Remarques
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Ajoutez vos remarques sur cet exercice..."
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
          Enregistrer l'évaluation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExerciseEvaluationModal;
